import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Layers, Maximize, Eye, Grid } from 'lucide-react';
// Helper component to render an SVG thumbnail card
function IconCard({
  title,
  subtitle,
  svgContent
}: {
  title: string;
  subtitle?: string;
  svgContent: React.ReactNode;
}) {
  return <div className="flex flex-col gap-3" data-id="element-2891">
      <div className="w-32 h-32 rounded-xl bg-slate-800 border border-slate-700 flex items-center justify-center overflow-hidden shadow-lg" data-id="element-2892">
        <svg xmlns="http://www.w3.org/2000/svg" width="120" height="120" fill="none" viewBox="0 0 120 120" data-id="element-2893">
          <rect width="120" height="120" rx="12" fill="#1e293b" data-id="element-2894" />
          {svgContent}
        </svg>
      </div>
      <div data-id="element-2895">
        <h4 className="text-sm font-bold text-white" data-id="element-2896">{title}</h4>
        {subtitle && <p className="text-xs text-slate-400 mt-0.5" data-id="element-2897">{subtitle}</p>}
      </div>
    </div>;
}
export function LayoutIconsPage() {
  return <div className="min-h-screen bg-slate-900 text-slate-300 font-sans selection:bg-cyan-500/30" data-id="element-2898">
      {/* Navigation Bar */}
      <div className="sticky top-0 z-50 bg-slate-900/80 backdrop-blur-xl border-b border-white/10 px-8 py-4 flex items-center justify-between" data-id="element-2899">
        <div className="flex items-center gap-4" data-id="element-2900">
          <Link to="/" className="p-2 -ml-2 rounded-full hover:bg-white/10 text-slate-400 hover:text-white transition-colors" data-id="element-2901">
            <ArrowLeft className="w-5 h-5" data-id="element-2902" />
          </Link>
          <div data-id="element-2903">
            <h1 className="text-lg font-bold text-white" data-id="element-2904">Layout Icon System</h1>
            <p className="text-xs text-slate-400" data-id="element-2905">Developer Reference</p>
          </div>
        </div>
        <div className="flex gap-4" data-id="element-2906">
          <Link to="/levels" className="text-sm font-medium text-slate-400 hover:text-white transition-colors" data-id="element-2907">
            View Levels System
          </Link>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-8 py-12 space-y-16" data-id="element-2908">
        {/* Header Section */}
        <section className="max-w-3xl" data-id="element-2909">
          <h2 className="text-4xl font-bold text-white mb-4 tracking-tight" data-id="element-2910">
            Composable Layout Icons
          </h2>
          <p className="text-lg text-slate-400 leading-relaxed" data-id="element-2911">
            A scalable, primitive-based icon system for floorplan and layout
            options. Instead of unique artwork for every permutation, we combine
            base shapes with modifiers to create instantly recognizable,
            consistent thumbnails.
          </p>
        </section>

        {/* Design Principles */}
        <section data-id="element-2912">
          <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-2" data-id="element-2913">
            <Layers className="w-5 h-5 text-cyan-400" data-id="element-2914" />
            Design Principles
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6" data-id="element-2915">
            {[{
            icon: Grid,
            title: 'Composable',
            desc: 'A small set of primitives combine into dozens of variations.'
          }, {
            icon: Maximize,
            title: 'Scalable',
            desc: "New layout types don't require custom art or new paradigms."
          }, {
            icon: Eye,
            title: 'Recognizable',
            desc: 'Optimized for instant readability at 96px thumbnail size.'
          }, {
            icon: Layers,
            title: 'Consistent',
            desc: 'Same visual language across all models, builders, and floors.'
          }].map((principle, i) => <div key={i} className="p-6 rounded-2xl bg-white/5 border border-white/10" data-id="element-2916">
                <principle.icon className="w-6 h-6 text-cyan-400 mb-4" data-id="element-2917" />
                <h4 className="text-base font-bold text-white mb-2" data-id="element-2918">
                  {principle.title}
                </h4>
                <p className="text-sm text-slate-400 leading-relaxed" data-id="element-2919">
                  {principle.desc}
                </p>
              </div>)}
          </div>
        </section>

        {/* Base Shapes */}
        <section data-id="element-2920">
          <div className="mb-8 border-b border-white/10 pb-4" data-id="element-2921">
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-2922">
              1. Base Shapes
            </h3>
            <p className="text-slate-400" data-id="element-2923">
              The foundation icon representing the scope of the layout change.
            </p>
          </div>
          <div className="flex flex-wrap gap-8" data-id="element-2924">
            <IconCard title="Single Floor" subtitle="Standard rectangle" svgContent={<rect x="25" y="35" width="70" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2926" />} data-id="element-2925" />
            <IconCard title="Multi-Floor" subtitle="Stacked with separator" svgContent={<>
                  <path d="M60 20L90 40L90 55L30 55L30 40Z" stroke="white" strokeWidth="3" strokeLinejoin="round" fill="none" data-id="element-2928" />
                  <path d="M60 55L90 55L90 95L30 95L30 55Z" stroke="white" strokeWidth="3" strokeLinejoin="round" fill="none" data-id="element-2929" />
                  <line x1="30" y1="55" x2="90" y2="55" stroke="white" strokeWidth="2" strokeDasharray="4 3" data-id="element-2930" />
                </>} data-id="element-2927" />
            <IconCard title="Whole House" subtitle="Roof + body" svgContent={<>
                  <path d="M60 25L90 45L90 85L30 85L30 45Z" stroke="white" strokeWidth="3.5" strokeLinejoin="round" fill="none" data-id="element-2932" />
                  <rect x="48" y="60" width="24" height="25" rx="2" stroke="white" strokeWidth="2.5" fill="none" data-id="element-2933" />
                  <line x1="60" y1="60" x2="60" y2="85" stroke="white" strokeWidth="2" data-id="element-2934" />
                </>} data-id="element-2931" />
            <IconCard title="L-Shape" subtitle="Offset footprint" svgContent={<path d="M25 25L70 25L70 55L95 55L95 95L25 95Z" stroke="white" strokeWidth="3.5" strokeLinejoin="round" fill="none" data-id="element-2936" />} data-id="element-2935" />
            <IconCard title="Split Level" subtitle="Offset rectangles" svgContent={<>
                  <rect x="20" y="40" width="45" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2938" />
                  <rect x="55" y="25" width="45" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2939" />
                </>} data-id="element-2937" />
          </div>
        </section>

        {/* Modifiers */}
        <section data-id="element-2940">
          <div className="mb-8 border-b border-white/10 pb-4" data-id="element-2941">
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-2942">2. Modifiers</h3>
            <p className="text-slate-400" data-id="element-2943">
              Overlays applied to base shapes to indicate what changes.
            </p>
          </div>
          <div className="flex flex-wrap gap-8" data-id="element-2944">
            <IconCard title="Room Dividers" subtitle="Solid interior lines" svgContent={<>
                  <rect x="25" y="35" width="70" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" opacity="0.3" data-id="element-2946" />
                  <line x1="60" y1="35" x2="60" y2="85" stroke="white" strokeWidth="2.5" data-id="element-2947" />
                  <line x1="25" y1="60" x2="60" y2="60" stroke="white" strokeWidth="2.5" data-id="element-2948" />
                </>} data-id="element-2945" />
            <IconCard title="Optional Walls" subtitle="Dashed lines" svgContent={<>
                  <rect x="25" y="35" width="70" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" opacity="0.3" data-id="element-2950" />
                  <line x1="60" y1="35" x2="60" y2="85" stroke="white" strokeWidth="2.5" strokeDasharray="4 3" data-id="element-2951" />
                </>} data-id="element-2949" />
            <IconCard title="Added Area" subtitle="Filled/dashed block" svgContent={<>
                  <rect x="25" y="35" width="55" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" opacity="0.3" data-id="element-2953" />
                  <path d="M80 45L95 45L95 75L80 75" stroke="white" strokeWidth="2.5" strokeDasharray="4 3" fill="none" data-id="element-2954" />
                  <rect x="80" y="45" width="15" height="30" fill="white" opacity="0.1" data-id="element-2955" />
                </>} data-id="element-2952" />
            <IconCard title="Removed Area" subtitle="Strikethrough / X" svgContent={<>
                  <rect x="25" y="35" width="70" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" opacity="0.3" data-id="element-2957" />
                  <line x1="60" y1="35" x2="60" y2="85" stroke="white" strokeWidth="2.5" data-id="element-2958" />
                  <line x1="65" y1="40" x2="90" y2="80" stroke="#ef4444" strokeWidth="2.5" data-id="element-2959" />
                  <line x1="90" y1="40" x2="65" y2="80" stroke="#ef4444" strokeWidth="2.5" data-id="element-2960" />
                </>} data-id="element-2956" />
            <IconCard title="Extension Arrows" subtitle="Directional expansion" svgContent={<>
                  <rect x="25" y="35" width="70" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" opacity="0.3" data-id="element-2962" />
                  <path d="M105 60L112 60M112 60L108 56M112 60L108 64" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2963" />
                  <path d="M15 60L8 60M8 60L12 56M8 60L12 64" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" data-id="element-2964" />
                </>} data-id="element-2961" />
            <IconCard title="Number Badge" subtitle="Option ordering" svgContent={<>
                  <rect x="25" y="35" width="70" height="50" rx="4" stroke="white" strokeWidth="3.5" fill="none" opacity="0.3" data-id="element-2966" />
                  <circle cx="95" cy="25" r="10" fill="#06b6d4" data-id="element-2967" />
                  <text x="95" y="29" fill="white" fontSize="12" fontWeight="bold" textAnchor="middle" data-id="element-2968">
                    2
                  </text>
                </>} data-id="element-2965" />
          </div>
        </section>

        {/* Combined Examples */}
        <section data-id="element-2969">
          <div className="mb-8 border-b border-white/10 pb-4" data-id="element-2970">
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-2971">
              3. Combined Examples
            </h3>
            <p className="text-slate-400" data-id="element-2972">
              How primitives compose into real-world layout options.
            </p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-8" data-id="element-2973">
            <IconCard title="Standard Basement" subtitle="Single floor, 3 dividers" svgContent={<>
                  <path d="M30 85L30 65L45 65L45 50L60 50L60 35L75 35L75 50L90 50L90 85" stroke="white" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="none" data-id="element-2975" />
                </>} data-id="element-2974" />
            <IconCard title="Legal Suite" subtitle="Bed + bath blocks" svgContent={<>
                  <rect x="30" y="50" width="60" height="30" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2977" />
                  <rect x="30" y="40" width="25" height="15" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2978" />
                  <circle cx="75" cy="45" r="8" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2979" />
                </>} data-id="element-2976" />
            <IconCard title="Legal Suite 2-Bed" subtitle="Two bed blocks" svgContent={<>
                  <rect x="20" y="50" width="35" height="28" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2981" />
                  <rect x="20" y="40" width="15" height="14" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2982" />
                  <rect x="65" y="50" width="35" height="28" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2983" />
                  <rect x="65" y="40" width="15" height="14" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2984" />
                  <circle cx="60" cy="45" r="6" stroke="white" strokeWidth="3" fill="none" data-id="element-2985" />
                </>} data-id="element-2980" />
            <IconCard title="Standard Main" subtitle="Kitchen/living split" svgContent={<>
                  <rect x="25" y="30" width="70" height="55" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2987" />
                  <line x1="60" y1="30" x2="60" y2="85" stroke="white" strokeWidth="2.5" data-id="element-2988" />
                  <line x1="25" y1="58" x2="60" y2="58" stroke="white" strokeWidth="2.5" data-id="element-2989" />
                </>} data-id="element-2986" />
            <IconCard title="Extended Living" subtitle="Added area dashed" svgContent={<>
                  <rect x="20" y="30" width="80" height="55" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2991" />
                  <line x1="55" y1="30" x2="55" y2="85" stroke="white" strokeWidth="2.5" data-id="element-2992" />
                  <line x1="20" y1="55" x2="55" y2="55" stroke="white" strokeWidth="2.5" data-id="element-2993" />
                  <path d="M70 50L85 50L85 70L70 70" stroke="white" strokeWidth="2" strokeDasharray="4 3" data-id="element-2994" />
                </>} data-id="element-2990" />
            <IconCard title="Open Concept" subtitle="Fewer dividers" svgContent={<>
                  <rect x="20" y="30" width="80" height="55" rx="4" stroke="white" strokeWidth="3.5" fill="none" data-id="element-2996" />
                  <line x1="20" y1="58" x2="50" y2="58" stroke="white" strokeWidth="2.5" data-id="element-2997" />
                  <line x1="50" y1="58" x2="50" y2="85" stroke="white" strokeWidth="2.5" data-id="element-2998" />
                </>} data-id="element-2995" />
            <IconCard title="3 Bedroom Upper" subtitle="3 room blocks" svgContent={<>
                  <rect x="20" y="28" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3000" />
                  <rect x="65" y="28" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3001" />
                  <rect x="35" y="65" width="50" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3002" />
                </>} data-id="element-2999" />
            <IconCard title="4 Bedroom Upper" subtitle="4 room blocks" svgContent={<>
                  <rect x="20" y="25" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3004" />
                  <rect x="65" y="25" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3005" />
                  <rect x="20" y="62" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3006" />
                  <rect x="65" y="62" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3007" />
                </>} data-id="element-3003" />
            <IconCard title="Loft Layout" subtitle="Large open area" svgContent={<>
                  <rect x="20" y="28" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3009" />
                  <rect x="65" y="28" width="35" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3010" />
                  <rect x="25" y="65" width="70" height="25" rx="3" stroke="white" strokeWidth="3" fill="none" data-id="element-3011" />
                  <line x1="60" y1="65" x2="60" y2="90" stroke="white" strokeWidth="2" strokeDasharray="4 3" data-id="element-3012" />
                </>} data-id="element-3008" />
            <IconCard title="Extended Standard" subtitle="Multi-floor base" svgContent={<>
                  <path d="M60 20L90 40L90 55L30 55L30 40Z" stroke="white" strokeWidth="3" strokeLinejoin="round" fill="none" data-id="element-3014" />
                  <path d="M60 55L90 55L90 95L30 95L30 55Z" stroke="white" strokeWidth="3" strokeLinejoin="round" fill="none" data-id="element-3015" />
                  <line x1="30" y1="55" x2="90" y2="55" stroke="white" strokeWidth="2" strokeDasharray="4 3" data-id="element-3016" />
                </>} data-id="element-3013" />
            <IconCard title="Extended Layout" subtitle="Multi-floor + added area" svgContent={<>
                  <path d="M60 20L95 42L95 57L25 57L25 42Z" stroke="white" strokeWidth="3" strokeLinejoin="round" fill="none" data-id="element-3018" />
                  <path d="M60 57L95 57L95 97L25 97L25 57Z" stroke="white" strokeWidth="3" strokeLinejoin="round" fill="none" data-id="element-3019" />
                  <line x1="25" y1="57" x2="95" y2="57" stroke="white" strokeWidth="2" strokeDasharray="4 3" data-id="element-3020" />
                  <circle cx="82" cy="47" r="4" fill="white" opacity="0.5" data-id="element-3021" />
                  <circle cx="82" cy="77" r="4" fill="white" opacity="0.5" data-id="element-3022" />
                  <path d="M75 43L89 43L89 51L75 51" stroke="white" strokeWidth="2" strokeDasharray="3 2" fill="none" data-id="element-3023" />
                  <path d="M75 73L89 73L89 81L75 81" stroke="white" strokeWidth="2" strokeDasharray="3 2" fill="none" data-id="element-3024" />
                </>} data-id="element-3017" />
            <IconCard title="Ground Floor" subtitle="Whole house single opt" svgContent={<>
                  <path d="M60 25L90 45L90 85L30 85L30 45Z" stroke="white" strokeWidth="3.5" strokeLinejoin="round" fill="none" data-id="element-3026" />
                  <rect x="48" y="60" width="24" height="25" rx="2" stroke="white" strokeWidth="2.5" fill="none" data-id="element-3027" />
                  <line x1="60" y1="60" x2="60" y2="85" stroke="white" strokeWidth="2" data-id="element-3028" />
                </>} data-id="element-3025" />
          </div>
        </section>

        {/* Usage Guidelines */}
        <section className="pb-20" data-id="element-3029">
          <div className="mb-8 border-b border-white/10 pb-4" data-id="element-3030">
            <h3 className="text-xl font-bold text-white mb-2" data-id="element-3031">
              4. Usage Guidelines
            </h3>
          </div>
          <div className="bg-white/5 border border-white/10 rounded-2xl p-8" data-id="element-3032">
            <ul className="space-y-4 text-slate-300" data-id="element-3033">
              <li className="flex gap-3" data-id="element-3034">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" data-id="element-3035" />
                <p data-id="element-3036">
                  <strong className="text-white" data-id="element-3037">Canvas & Background:</strong>{' '}
                  Always use a 120x120 viewBox with a dark slate background (
                  <code className="text-cyan-400 bg-cyan-400/10 px-1.5 py-0.5 rounded" data-id="element-3038">
                    #1e293b
                  </code>
                  ) and 12px rounded corners.
                </p>
              </li>
              <li className="flex gap-3" data-id="element-3039">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" data-id="element-3040" />
                <p data-id="element-3041">
                  <strong className="text-white" data-id="element-3042">Stroke Properties:</strong> Use
                  white strokes. Base outlines should be 3-3.5px wide. Interior
                  lines and modifiers should be 2-2.5px wide.
                </p>
              </li>
              <li className="flex gap-3" data-id="element-3043">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" data-id="element-3044" />
                <p data-id="element-3045">
                  <strong className="text-white" data-id="element-3046">Dashed Lines:</strong> Use{' '}
                  <code className="text-cyan-400 bg-cyan-400/10 px-1.5 py-0.5 rounded" data-id="element-3047">
                    stroke-dasharray="4 3"
                  </code>{' '}
                  for optional, removable, or extended elements.
                </p>
              </li>
              <li className="flex gap-3" data-id="element-3048">
                <div className="w-1.5 h-1.5 rounded-full bg-cyan-400 mt-2 flex-shrink-0" data-id="element-3049" />
                <p data-id="element-3050">
                  <strong className="text-white" data-id="element-3051">Corner Radii:</strong> Apply{' '}
                  <code className="text-cyan-400 bg-cyan-400/10 px-1.5 py-0.5 rounded" data-id="element-3052">
                    rx="4"
                  </code>{' '}
                  to standard room blocks, and{' '}
                  <code className="text-cyan-400 bg-cyan-400/10 px-1.5 py-0.5 rounded" data-id="element-3053">
                    rx="3"
                  </code>{' '}
                  to smaller blocks (like bedrooms).
                </p>
              </li>
            </ul>
          </div>
        </section>
      </div>
    </div>;
}